import React from 'react';
import { Box, Collapse, Container, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useAssistant } from './AssistantContext';
import { tokens } from '../theme';

function renderInline(text, keyPrefix) {
  // Only bold is supported: the server writes plain sentences, so anything
  // heavier would be noise.
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    return bold
      ? <Box key={`${keyPrefix}-${index}`} component="strong" sx={{ fontWeight: 700 }}>{bold[1]}</Box>
      : <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
  });
}

export function renderAssistantText(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <Box key={`ul-${blocks.length}`} component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.35 }}>
        {bullets.map((item, index) => (
          <Typography key={`li-${index}`} component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
            {renderInline(item, `li-${blocks.length}-${index}`)}
          </Typography>
        ))}
      </Box>
    );
    bullets = [];
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushBullets();
      return;
    }
    const bullet = trimmed.match(/^(?:[-*•]|\d+[.)])\s+(.+)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flushBullets();
    blocks.push(
      <Typography key={`p-${blocks.length}`} variant="body2" sx={{ lineHeight: 1.55 }}>
        {renderInline(trimmed, `p-${blocks.length}`)}
      </Typography>
    );
  });
  flushBullets();
  return blocks;
}

const toneColors = {
  info: { border: tokens.mintLine, icon: tokens.mint, bg: 'rgba(53,240,194,0.06)' },
  warning: { border: 'rgba(255,194,102,0.5)', icon: tokens.warn, bg: 'rgba(255,194,102,0.06)' },
  error: { border: 'rgba(255,107,122,0.5)', icon: tokens.danger, bg: 'rgba(255,107,122,0.06)' },
};

export default function AssistantAnswer() {
  const { answer, clearAnswer } = useAssistant();
  const tone = toneColors[answer?.tone] || toneColors.info;
  return (
    <Collapse in={Boolean(answer)} unmountOnExit>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1.5, md: 2 } }}>
        <Box
          data-testid="assistant-answer"
          data-assistant-mode={answer?.mode || ''}
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            p: 1.5,
            pr: 1,
            border: '1px solid',
            borderColor: tone.border,
            borderRadius: '10px',
            bgcolor: tone.bg,
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ color: tone.icon, fontSize: 20, mt: '2px', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 0.75 }}>
            {renderAssistantText(answer?.text || '')}
          </Box>
          <IconButton size="small" aria-label="Dismiss" onClick={clearAnswer} sx={{ flexShrink: 0, mt: '-2px' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Container>
    </Collapse>
  );
}
